import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Row,
  Col,
  Button,
  Space,
  message,
  Layout,
  Modal,
  Input,
  Select,
} from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import {
  createMotif,
  getMotifs,
  updateMotif,
} from "../api/motifs-annulation/motifRequests";
import PermissionRender from "./PermissionRender";

const { Content } = Layout;

// You'll need to create these API functions

function CancellationReseans() {
  const [motifs, setMotifs] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentMotif, setCurrentMotif] = useState({
    id: null,
    name: "",
    description: "",
    type: "warning",
    status: "enable",
  });
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  useEffect(() => {
    fetchMotifs();
  }, []);

  const fetchMotifs = async () => {
    setLoading(true);
    try {
      const data = await getMotifs();
      setMotifs(data);
    } catch (error) {
      message.error("Échec du chargement des motifs d'annulation");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMotif = async () => {
    if (currentMotif.name.trim()) {
      try {
        if (isEditMode) {
          await updateMotif(currentMotif.id, currentMotif);
          message.success("Motif d'annulation modifié avec succès");
        } else {
          await createMotif(currentMotif);
          message.success("Motif d'annulation ajouté avec succès");
        }
        resetForm();
        fetchMotifs();
      } catch (error) {
        message.error("Erreur lors de l'enregistrement du motif d'annulation");
      }
    } else {
      message.error("Veuillez remplir tous les champs obligatoires");
    }
  };

  const handleEditMotif = (motif) => {
    setCurrentMotif({ ...motif });
    setIsEditMode(true);
    setIsModalVisible(true);
  };

  const resetForm = () => {
    setCurrentMotif({
      id: null,
      name: "",
      description: "",
      type: "warning",
      status: "enable",
    });
    setIsEditMode(false);
    setIsModalVisible(false);
  };

  const columns = [
    {
      title: "Id Motif",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Libellé",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      // ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      filters: [
        { text: "Avertissement", value: "warning" },
        { text: "Infraction", value: "infraction" },
        { text: "Abonnement", value: "subscription" },
      ],
      onFilter: (value, record) => record.type === value,
      render: (type) => (
        <span
          className={
            type === "warning"
              ? "text-yellow-600"
              : type === "subscription"
              ? "text-blue-600"
              : "text-red-600"
          }
        >
          {type === "warning"
            ? "Avertissement"
            : type === "subscription"
            ? "Abonnement"
            : "Infraction"}
        </span>
      ),
    },
    {
      title: "Statut",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span
          className={status === "enable" ? "text-green-600" : "text-red-600"}
        >
          {status === "enable" ? "Actif" : "Inactif"}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      render: (_, record) => (
        <Space size="middle">
          <PermissionRender
            requiredPermission={["motifs_annulation", "update"]}
          >
            <Button
              icon={<EditOutlined />}
              onClick={() => handleEditMotif(record)}
            >
              Modifier
            </Button>
          </PermissionRender>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  return (
    <Layout className="min-h-screen">
      <Content className="py-6 sm:px-6 lg:px-8">
        <Card className="mb-6 shadow-sm">
          <Row gutter={16}>
            <Col xs={24} sm={24} md={24}>
              <PermissionRender
                requiredPermission={["motifs_annulation", "create"]}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    resetForm();
                    setIsModalVisible(true);
                  }}
                  className="w-full"
                >
                  Ajouter un motif d'annulation
                </Button>
              </PermissionRender>
            </Col>
          </Row>
        </Card>

        <Card className="shadow-sm">
          <Table
            dataSource={motifs.map((motif) => ({ key: motif.id, ...motif }))}
            columns={columns}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} motifs`,
            }}
            loading={loading}
            onChange={handleTableChange}
          />
        </Card>

        <Modal
          title={
            isEditMode
              ? "Modifier le motif d'annulation"
              : "Ajouter un motif d'annulation"
          }
          visible={isModalVisible}
          onOk={handleAddMotif}
          onCancel={resetForm}
          width={600}
        >
          <div className="space-y-4">
            <Input
              placeholder="Libellé"
              value={currentMotif.name}
              onChange={(e) =>
                setCurrentMotif({ ...currentMotif, name: e.target.value })
              }
            />

            <Input.TextArea
              placeholder="Description"
              value={currentMotif.description}
              onChange={(e) =>
                setCurrentMotif({
                  ...currentMotif,
                  description: e.target.value,
                })
              }
              rows={4}
            />

            <Select
              placeholder="Type"
              value={currentMotif.type}
              onChange={(value) =>
                setCurrentMotif({ ...currentMotif, type: value })
              }
              style={{ width: "100%" }}
            >
              <Select.Option value="warning">Avertissement</Select.Option>
              <Select.Option value="infraction">Infraction</Select.Option>
              <Select.Option value="subscription">Abonnement</Select.Option>
            </Select>

            <Select
              placeholder="Statut"
              value={currentMotif.status}
              onChange={(value) =>
                setCurrentMotif({ ...currentMotif, status: value })
              }
              style={{ width: "100%" }}
            >
              <Select.Option value="enable">Actif</Select.Option>
              <Select.Option value="disable">Inactif</Select.Option>
            </Select>
          </div>
        </Modal>
      </Content>
    </Layout>
  );
}

export default CancellationReseans;
